/**
 * Turning a stored `linkPath` into somewhere this app can actually go.
 *
 * The API writes a route name; the router owns the routes. Those two lists had
 * drifted completely apart — the templates emitted `/examinations`,
 * `/appointments`, `/consent`, `/results` and `/certificates`, and not one of
 * them is a route this app declares. Every notification tap rendered "That page
 * does not exist", including the consent prompt, which is the only notification
 * a worker can act on themselves.
 *
 * Resolving rather than navigating directly is the fix, and it is worth doing
 * even now that both sides agree, for two reasons:
 *
 *   · rows outlive renames. A notification queued before a route was renamed
 *     still holds the old path, and it is still the thing the worker is being
 *     asked to do. The migration repaired the rows that existed; the aliases
 *     below cover any that were queued between deploys, and any future rename.
 *
 *   · one path is deliberately incomplete. The consent screen is
 *     `/consent/:orderId`, and the order id is on the row as `entityId` rather
 *     than baked into the path — carrying it twice would let the two disagree.
 *
 * The canonical values are defined once, on the server, in
 * `apps/api/src/notification/services/notification-links.ts`. The API compiles
 * to CommonJS and cannot share a module with this app, so the union below is
 * the mirror — and `RESOLVERS` is exhaustive over it, which makes a missing
 * entry a compile error rather than a 404 a worker discovers.
 */

/** Mirrors `EmployeeLink` on the server. Keep the two in step. */
export type EmployeeLink = '/examination' | '/records' | '/fitness' | '/consent';

/** The subset of a notification this needs. Everything here is already returned. */
export interface LinkableNotification {
  linkPath: string | null;
  entityType?: string | null;
  entityId?: string | null;
}

/** Where a worker lands when a notification has nowhere better to send them. */
const FALLBACK = '/home';

/**
 * Exhaustive over EmployeeLink: adding a link on the server and forgetting it
 * here fails the build.
 */
const RESOLVERS: Record<EmployeeLink, (n: LinkableNotification) => string> = {
  '/examination': () => '/examination',
  '/records': () => '/records',
  '/fitness': () => '/fitness',
  /**
   * The consent screen needs the examination order it is about. A consent
   * notification queued without one has nowhere to go, and Home is a better
   * answer than a URL that renders an error — the pending-consent card is
   * waiting there anyway.
   */
  '/consent': (n) =>
    n.entityType === 'ExaminationOrder' && n.entityId ? `/consent/${n.entityId}` : FALLBACK,
};

/**
 * Paths this app used to be sent, from rows queued before the templates were
 * corrected. Kept separate from the canonical table so it is obvious which
 * entries are history and can be dropped once no such rows remain.
 */
const LEGACY: Record<string, EmployeeLink> = {
  '/examinations': '/examination',
  '/appointments': '/examination',
  '/results': '/records',
  '/certificates': '/fitness',
};

/**
 * The route to navigate to, or null when there is nothing to open.
 *
 * Null rather than the fallback, so the caller can tell "this notification is
 * informational" apart from "this notification is broken" — the first should
 * stay put and mark itself read, the second should still take the worker
 * somewhere useful.
 */
export function resolveNotificationLink(n: LinkableNotification): string | null {
  if (!n.linkPath) return null;

  const canonical = (LEGACY[n.linkPath] ?? n.linkPath) as EmployeeLink;
  const resolve = RESOLVERS[canonical];

  // An unknown path means a newer API against an older app — a deploy skew
  // that will resolve itself. Home is the honest answer in the meantime; a
  // 404 tells the worker their app is broken.
  return resolve ? resolve(n) : FALLBACK;
}
